/**
 * DemoToPrivatePanel — DEMO_TO_PRIVATE_MIGRATION_V1
 *
 * Shows a fixture-preloaded GWU so new users can explore the full governance
 * lifecycle (timeline, receipts, relationships, verification) without uploading
 * files or creating an account first.
 *
 * "Clone to My Workspace" navigates to the Guided Flow with the fixture's
 * metadata as prefill context — it does NOT copy server-side state.
 *
 * BOUNDARY:
 * - Demo panel = navigation aid / onboarding surface
 * - Demo panel ≠ real GWU creation
 * - Demo panel ≠ approval or execution
 * - "Clone" opens the guided flow; it does NOT create a GWU automatically
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Clock,
  CheckCircle,
  Network,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Shield,
  AlertTriangle,
  Receipt,
  History,
} from 'lucide-react';
import { DEMO_FIXTURE_GWU } from './demoFixtureGWU';
import { saveLifecycleContext } from '../../components/lifecycle/WorkLifecycleContext';

type TabId = 'overview' | 'timeline' | 'receipts' | 'relationships' | 'next-moves';

const TABS: Array<{ id: TabId; label: string; icon: React.ReactNode }> = [
  { id: 'overview', label: 'Overview', icon: <FileText className="w-3.5 h-3.5" /> },
  { id: 'timeline', label: 'Timeline', icon: <History className="w-3.5 h-3.5" /> },
  { id: 'receipts', label: 'Receipts', icon: <Receipt className="w-3.5 h-3.5" /> },
  { id: 'relationships', label: 'Relationships', icon: <Network className="w-3.5 h-3.5" /> },
  { id: 'next-moves', label: 'Next Moves', icon: <ChevronRight className="w-3.5 h-3.5" /> },
];

function relativeTime(isoString: string): string {
  const delta = Date.now() - new Date(isoString).getTime();
  const hours = Math.floor(delta / 3600000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface DemoToPrivatePanelProps {
  className?: string;
}

export const DemoToPrivatePanel: React.FC<DemoToPrivatePanelProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [showBurdens, setShowBurdens] = useState(false);
  const [cloneClicked, setCloneClicked] = useState(false);

  const gwu = DEMO_FIXTURE_GWU;

  const handleCloneToWorkspace = () => {
    // Save demo context as navigation memory only — not a real GWU
    saveLifecycleContext({
      label: `Clone of: ${gwu.name}`,
    });
    setCloneClicked(true);
    // Navigate to guided flow — user creates their own real GWU there
    setTimeout(() => navigate('/formation-gate/guided-flow'), 400);
  };

  return (
    <div className={`bg-slate-900 border border-slate-800 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-slate-800">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-indigo-900/40 border border-indigo-700/40 rounded text-indigo-400 text-xs font-medium">
                Demo Workspace
              </span>
              <span className="text-xs text-slate-500">fixture-only • no real data</span>
            </div>
            <h3 className="text-sm font-semibold text-white">{gwu.name}</h3>
            <p className="text-xs text-slate-400 mt-0.5 leading-relaxed max-w-xl">{gwu.description}</p>
          </div>

          {/* Clone CTA */}
          <div className="flex-shrink-0">
            <button
              onClick={handleCloneToWorkspace}
              disabled={cloneClicked}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 text-white text-xs font-medium rounded-lg transition-colors"
            >
              {cloneClicked
                ? <><CheckCircle className="w-3.5 h-3.5" /> Starting...</>
                : <><ArrowRight className="w-3.5 h-3.5" /> Clone to My Workspace</>}
            </button>
          </div>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-3 mt-3 text-xs text-slate-500 flex-wrap">
          <span className="flex items-center gap-1">
            <Shield className="w-3 h-3" />
            proof-only
          </span>
          <span className="flex items-center gap-1">
            <FileText className="w-3 h-3" />
            {gwu.artifact.fileName}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            created {relativeTime(gwu.createdAt)}
          </span>
          <span className="font-mono text-slate-600">
            gwu:{gwu.gwuId.slice(0, 14)}…
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-800 overflow-x-auto">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
              activeTab === tab.id
                ? 'text-indigo-400 border-indigo-500'
                : 'text-slate-500 hover:text-slate-300 border-transparent'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-5">

        {/* Overview */}
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {/* Artifact */}
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Artifact</p>
              <div className="flex items-start gap-3 p-3 bg-slate-800/60 border border-slate-700/60 rounded-lg">
                <FileText className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm text-slate-200">{gwu.artifact.fileName}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5 break-all">
                    {gwu.artifact.artifactHash.slice(0, 40)}…
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{gwu.artifact.sizeLabel}</div>
                </div>
                <span className="ml-auto flex-shrink-0 text-xs text-green-400 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> hashed
                </span>
              </div>
            </div>

            {/* Continuity burdens */}
            <div>
              <button
                onClick={() => setShowBurdens(v => !v)}
                className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-400 mb-2"
              >
                <AlertTriangle className="w-3 h-3 text-amber-600" />
                {gwu.continuityBurdens.length} continuity burden{gwu.continuityBurdens.length !== 1 ? 's' : ''}
                <ChevronDown className={`w-3 h-3 transition-transform ${showBurdens ? 'rotate-180' : ''}`} />
              </button>
              {showBurdens && (
                <div className="space-y-1.5 ml-4">
                  {gwu.continuityBurdens.map(b => (
                    <div key={b.id} className="flex items-start gap-2 text-xs">
                      <span className={`mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full ${
                        b.priority === 'high' ? 'bg-red-500' : 'bg-amber-500'
                      }`} />
                      <span className="text-slate-400">{b.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats summary */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: 'Timeline Events', value: gwu.timeline.length },
                { label: 'Receipts', value: gwu.receipts.length },
                { label: 'Relationships', value: gwu.relationships.length },
              ].map(s => (
                <div key={s.label} className="p-3 bg-slate-800/40 border border-slate-800 rounded-lg text-center">
                  <div className="text-lg font-bold text-white">{s.value}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Timeline */}
        {activeTab === 'timeline' && (
          <div className="space-y-0">
            {gwu.timeline.map((evt, i) => (
              <div key={evt.id} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${
                    evt.type === 'verified' ? 'bg-green-500' :
                    evt.type === 'receipt_issued' ? 'bg-blue-500' :
                    evt.type === 'relationship_linked' ? 'bg-indigo-500' :
                    'bg-slate-500'
                  }`} />
                  {i < gwu.timeline.length - 1 && (
                    <div className="w-px flex-1 bg-slate-800 my-1" />
                  )}
                </div>
                <div className="pb-4 min-w-0">
                  <div className="text-xs font-medium text-slate-200">{evt.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {relativeTime(evt.timestamp)} · {evt.actor}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Receipts */}
        {activeTab === 'receipts' && (
          <div className="space-y-2">
            {gwu.receipts.map(r => (
              <div key={r.receiptId} className="flex items-start gap-3 p-3 bg-slate-800/60 border border-slate-700/60 rounded-lg">
                <Receipt className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-slate-200">{r.type}</span>
                    {r.verified && (
                      <span className="text-xs text-green-400 flex items-center gap-0.5">
                        <CheckCircle className="w-3 h-3" /> verified
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">
                    {r.receiptId} · {relativeTime(r.issuedAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Relationships */}
        {activeTab === 'relationships' && (
          <div className="space-y-2">
            {gwu.relationships.map(r => (
              <div key={r.relationshipId} className="flex items-start gap-3 p-3 bg-slate-800/60 border border-slate-700/60 rounded-lg">
                <Network className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-200">{r.relationshipType}</div>
                  <div className="text-xs text-slate-500 font-mono mt-0.5">
                    → {r.targetGwuId}
                  </div>
                  <div className="text-xs text-slate-500">{relativeTime(r.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Next Moves */}
        {activeTab === 'next-moves' && (
          <div className="space-y-2">
            {gwu.suggestedNextMoves.map(m => (
              <div key={m.id} className="flex items-start gap-3 p-3 bg-slate-800/60 border border-slate-700/60 rounded-lg">
                <ChevronRight className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-200">{m.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{m.rationale}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer boundary notice */}
      <div className="px-5 pb-4">
        <p className="text-xs text-slate-600 border-t border-slate-800 pt-3">
          Fixture data only — no real operations. "Clone to My Workspace" opens the guided flow to create your own GWU.
        </p>
      </div>
    </div>
  );
};
